/**
 * One dataset's workspace: its sources, their status, adding and reviewing them, and
 * cloning it or exporting it as a clone package. Polls while the worker is still moving
 * a source. specs/dataset-workspace.md, specs/clone-packages.md
 *
 * Uploading PDFs is the default way in (#891); the Excel source list and a single source by
 * link are the alternatives. Links that couldn't be downloaded are listed for doing by hand.
 *
 * What the viewer sees follows their grant on the dataset (specs/access-model.md, #799):
 * view reads, contribute adds and fixes sources until approval, manage reviews and
 * runs the settings. Cloning stays with admins, as on the server.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Copy, Database, FileText, Settings2 } from 'lucide-react';
import { Button } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useAuth } from '../../../components/AuthGate';
import { hasRole } from '../../../utils/roles';
import { canOnResource } from '../../../utils/access';
import { getDataset, listSources, ACTIVE_STATUSES } from './datasetApi';
import AddSourceForm from './AddSourceForm';
import ImportPanel from './ImportPanel';
import PdfUploadPanel from './PdfUploadPanel';
import ManualDownloads from './ManualDownloads';
import IndexedDocuments from './IndexedDocuments';
import SourceKindTagging from './SourceKindTagging';
import DatasetSettings from './DatasetSettings';
import SourceTable from './SourceTable';
import ClonePanel from '../../../components/Clone/ClonePanel';
import { Section, SectionTitle, Meta, ErrorText } from './styles';

const POLL_MS = 4000;

export default function DatasetWorkspace({ datasetId, onBack, onOpenDataset }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = hasRole(user, 'admin');
  const canManage = isAdmin || canOnResource(user, 'dataset', datasetId, 'manage');
  const canContribute = canManage || canOnResource(user, 'dataset', datasetId, 'contribute');
  const [dataset, setDataset] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const loadSources = useCallback(async () => {
    try {
      setSources(await listSources(datasetId));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [datasetId]);

  useEffect(() => {
    getDataset(datasetId).then(setDataset).catch((err) => setError(err.message));
    loadSources();
  }, [datasetId, loadSources]);

  const busy = sources.some((s) => ACTIVE_STATUSES.has(s.status));
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(loadSources, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, loadSources]);

  const instances = dataset?.instances || [];

  return (
    <>
      <Button $variant="ghost" $size="small" onClick={onBack} style={{ marginBottom: 16 }}>
        <ArrowLeft /> {t('datasetWorkspace.back')}
      </Button>

      <Section>
        <SectionTitle><Database size={18} /> {dataset?.name || datasetId}</SectionTitle>
        <Meta>
          <code>{datasetId}</code>
          {dataset?.topic && <span>{dataset.topic}</span>}
          {dataset?.language && <span>{dataset.language}</span>}
          <span>
            {dataset?.is_global
              ? t('datasetWorkspace.global')
              : instances.length
                ? t('datasetWorkspace.attachedTo', { instances: instances.map((i) => i.title).join(', ') })
                : t('datasetWorkspace.notAttached')}
          </span>
          {canManage && dataset && !editing && (
            <Button $variant="ghost" $size="small" onClick={() => setEditing(true)}>
              <Settings2 /> {t('datasetWorkspace.settings.open')}
            </Button>
          )}
        </Meta>
        {dataset?.description && !editing && <Meta>{dataset.description}</Meta>}
        {editing && (
          <DatasetSettings
            dataset={dataset}
            onCancel={() => setEditing(false)}
            onSaved={(saved) => { setDataset({ ...dataset, ...saved }); setEditing(false); }}
          />
        )}
      </Section>

      {canContribute && (
        <>
          <PdfUploadPanel datasetId={datasetId} onUploaded={loadSources} />
          <ImportPanel datasetId={datasetId} onConfirmed={loadSources} />
          <ManualDownloads datasetId={datasetId} sources={sources} onChanged={loadSources} />
        </>
      )}

      <Section>
        <SectionTitle><FileText size={18} /> {t('datasetWorkspace.sourcesTitle')}</SectionTitle>
        {error && <ErrorText>{error}</ErrorText>}
        <SourceTable datasetId={datasetId} sources={sources} canApprove={canManage}
          canContribute={canContribute} onChanged={loadSources} />
      </Section>

      <IndexedDocuments datasetId={datasetId} canManage={canManage} />

      {/* Tagging untagged sources is an admin-only AI job (specs/knowledge-kinds.md, step 6) */}
      {isAdmin && <SourceKindTagging datasetId={datasetId} onDone={loadSources} />}

      {canContribute && <AddSourceForm datasetId={datasetId} onAdded={loadSources} />}

      {isAdmin && (
        <Section>
          <SectionTitle><Copy size={18} /> {t('clone.datasetSectionTitle')}</SectionTitle>
          <ClonePanel kind="dataset" sourceId={datasetId} sourceName={dataset?.name}
            onOpenClone={onOpenDataset && ((r) => onOpenDataset(r.id))} />
        </Section>
      )}
    </>
  );
}
